import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useBook } from '../contexts/BookContext';
import { getCloudFunctionUrl } from '../config/firebaseFunctionUrl';
import StyleModal from '../components/StyleSelector/StyleModal';
import PDFShippingPreview from '../components/PDFShipping/PDFShippingPreview';
import { notify, toastIds } from '../utils/notify.jsx';
import {
  bindingOptions,
  interiorColorOptions,
  paperOptions,
  coverOptions,
  linenOptions,
  foilOptions,
  printingRules,
} from '../data/printingOptions';
import {
  createPrintJobCostCalculation,
  getShippingOptions,
} from '../services/printService/luluApi';
import { getStates } from '../services/locationService';
import { countries } from '../utils/countries';
import { getBook, updateBookPdfUrls } from '../services/bookService';
import { generateVerticalPDF, generateCoverPDF } from '../services/pdfService';
import { uploadPDF } from '../services/firebase/storage';
import { getAuth } from 'firebase/auth';
import TutorialModal from '../components/TutorialSlideshow/TutorialModal';

export default function ShipPage() {
  const navigate = useNavigate();
  const { bookId } = useParams();
  const location = useLocation();
  const { currentBook, loadBook } = useBook();
  const proceedButtonRef = useRef(null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const [savedPdfUrls, setSavedPdfUrls] = useState({
    cover: null,
    verticalContent: null,
    horizontalContent: null
  });
  const [isGeneratingPdfs, setIsGeneratingPdfs] = useState(false);
  const [lastGeneratedBinding, setLastGeneratedBinding] = useState(null);
  const [isFinalizingOrder, setIsFinalizingOrder] = useState(false);
  
  useEffect(() => {
    if (bookId) {
      loadBook(bookId);
      // Load the saved PDF urls for this book
      const fetchBookDetails = async () => {
        try {
          const bookDetails = await getBook(bookId);
          if (bookDetails && bookDetails.pdfUrls) {
            setSavedPdfUrls(bookDetails.pdfUrls);
          }
        } catch (error) {
          console.error('Error fetching book details:', error);
          notify.error('Error loading book information', { toastId: `ship-load-${bookId}` });
        }
      };
      fetchBookDetails();
    }
  }, [bookId, loadBook]);

  // Success handling moved to dedicated CheckoutSuccessPage

  const [selectedOptions, setSelectedOptions] = useState({
    bindingOption: null,
    interiorColorOption: null,
    paperOption: null,
    coverOption: null,
    linenOption: null,
    foilOption: null,
    pageCount: 32,
  });

  const [uiState, setUiState] = useState({
    showUploadSection: false,
    showShippingSection: false
  });

  // Check if print options are complete
  const arePrintOptionsComplete = () => {
    const { bindingOption, interiorColorOption, paperOption } = selectedOptions;
    return bindingOption && interiorColorOption && paperOption;
  };

  // Update UI state when options or PDF status changes
  useEffect(() => {
    const optionsComplete = arePrintOptionsComplete();
    const bindingId = (typeof selectedOptions.bindingOption === 'string'
      ? selectedOptions.bindingOption
      : selectedOptions.bindingOption?.id) || null;
    setUiState(prev => ({
      ...prev,
      // Show Book Files as soon as a binding is selected so generation UI is visible
      showUploadSection: !!bindingId,
      // Show Shipping section as soon as a binding is selected
      showShippingSection: !!bindingId
    }));
  }, [selectedOptions]);

  // (moved below handlePDFsReady)

  const [shippingDetails, setShippingDetails] = useState({
    quantity: 1,
    countryCode: '',
    countryName: '',
    method: '',
    name: '',
    street1: '',
    street2: '',
    city: '',
    stateCode: '',
    postalCode: '',
    phoneNumber: '',
    estimatedCost: null,
    subtotal: null,
    shippingCost: null,
    tax: null,
    loading: false,
    error: null,
    suggestedAddress: null,
    addressWarnings: [],
    coverPdfUrl: null,
    interiorPdfUrl: null
  });

  // Unit price mapping based on interior color selection
  function getUnitPrice() {
    const id = selectedOptions.interiorColorOption?.id;
    if (id === 'premium-color') return 24.99;
    // Default and for standard-color
    return 19.99;
  }

  // Keep subtotal in sync with selection and quantity even before shipping calc
  useEffect(() => {
    const bookSubtotal = getUnitPrice() * (shippingDetails.quantity || 1);
    setShippingDetails((prev) =>
      prev.subtotal === bookSubtotal ? prev : { ...prev, subtotal: bookSubtotal }
    );
  }, [selectedOptions.interiorColorOption, shippingDetails.quantity]);

  const lastReadyRef = useRef({ cover: null, interior: null });
  const pendingShippingDetailsRef = useRef(null);

  // Handle when PDFs are ready for shipping
  const handlePDFsReady = useCallback((pdfDetails) => {
    // Set PDF URLs in shipping details
    setShippingDetails(prev => ({
      ...prev,
      coverPdfUrl: pdfDetails.coverPdfUrl,
      interiorPdfUrl: pdfDetails.interiorPdfUrl
    }));
    
    if (
      lastReadyRef.current.cover === pdfDetails.coverPdfUrl &&
      lastReadyRef.current.interior === pdfDetails.interiorPdfUrl
    ) {
      return;
    }
    lastReadyRef.current = {
      cover: pdfDetails.coverPdfUrl,
      interior: pdfDetails.interiorPdfUrl
    };
    console.log("PDFs ready for shipping:", pdfDetails);

  }, [bookId]);

  const generatePdfs = useCallback(async (force = false) => {
    if (!currentBook || isGeneratingPdfs) return;
    try {
      setIsGeneratingPdfs(true);
      const toastId = notify.loading('Preparing book files (cover and interior)...', {
        toastId: toastIds.shipPdf(bookId),
      });

      // Ensure the user is authenticated before uploading to Firebase Storage
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        notify.update(toastId, { render: 'Please sign in to generate and upload book files.', type: 'error', isLoading: false, autoClose: 3000 });
        setIsGeneratingPdfs(false);
        return;
      }

      // FRESH FETCH: Get latest book data from Firestore before generating PDFs
      // This ensures any backend-created image elements are included
      let freshBook = currentBook;
      try {
        console.log('📚 Fetching fresh book data before PDF generation (Ship page)...');
        freshBook = await getBook(currentBook.id);
        console.log('✓ Fresh book data loaded with spreads:', Object.keys(freshBook.spreads || {}));
      } catch (fetchErr) {
        console.warn('⚠️ Could not fetch fresh book data, using cached version:', fetchErr.message);
        // Fall back to cached book if fresh fetch fails
      }

      const bindingType = (typeof selectedOptions.bindingOption === 'string'
        ? selectedOptions.bindingOption
        : selectedOptions.bindingOption?.id) || 'perfect-bound';
      const coverPdf = await generateCoverPDF(freshBook, { forLuluPrinting: true, bindingType });
      const interiorPdf = await generateVerticalPDF(freshBook, { forLuluPrinting: true, skipCover: true, bindingType });

      const [coverUp, interiorUp] = await Promise.all([
        uploadPDF(coverPdf, bookId, 'cover'),
        uploadPDF(interiorPdf, bookId, 'verticalContent')
      ]);

      if (!coverUp?.url || !interiorUp?.url) throw new Error('Failed to upload generated PDFs');

      await updateBookPdfUrls(bookId, { cover: coverUp.url, verticalContent: interiorUp.url });
      setSavedPdfUrls(prev => ({ ...prev, cover: coverUp.url, verticalContent: interiorUp.url }));
      handlePDFsReady({ coverPdfUrl: coverUp.url, interiorPdfUrl: interiorUp.url });
      setLastGeneratedBinding(bindingType);

      notify.update(toastId, { render: 'Book files are ready', type: 'success', isLoading: false, autoClose: 2000 });
    } catch (err) {
      console.error('Auto-generate PDFs error:', err);
      notify.update(toastIds.shipPdf(bookId), { render: err?.message || 'Failed to prepare book files', type: 'error', isLoading: false, autoClose: 3500 });
    } finally {
      setIsGeneratingPdfs(false);
    }
  }, [currentBook, selectedOptions.bindingOption, bookId, isGeneratingPdfs, handlePDFsReady]);

  // Generate as soon as a binding is selected (optimize perceived speed)
  useEffect(() => {
    const bindingId = (typeof selectedOptions.bindingOption === 'string'
      ? selectedOptions.bindingOption
      : selectedOptions.bindingOption?.id) || null;
    if (!bindingId) return;
    if (isGeneratingPdfs) return;
    if (lastGeneratedBinding === bindingId) return;
    generatePdfs(true);
  }, [selectedOptions.bindingOption, lastGeneratedBinding, isGeneratingPdfs, generatePdfs]);

  // Auto-generate PDFs once print options are complete (for testing flow)
  useEffect(() => {
    const optionsComplete = arePrintOptionsComplete();
    const params = new URLSearchParams(location.search || '');
    const forceGen = params.get('autogen') === '1';
    const missing = !savedPdfUrls?.cover || !savedPdfUrls?.verticalContent;
    const shouldGenerate = optionsComplete && uiState.showUploadSection && (missing || forceGen) && currentBook && !isGeneratingPdfs;
    if (!shouldGenerate) return;
    generatePdfs(true);
  }, [uiState.showUploadSection, currentBook, savedPdfUrls, selectedOptions.bindingOption, bookId, handlePDFsReady, isGeneratingPdfs, location.search, generatePdfs]);

  // Check for existing PDFs on component mount
  useEffect(() => {
    if (savedPdfUrls?.cover && savedPdfUrls?.verticalContent) {
      handlePDFsReady({
        coverPdfUrl: savedPdfUrls.cover,
        interiorPdfUrl: savedPdfUrls.verticalContent
      });
    }
  }, [savedPdfUrls, handlePDFsReady]);

  const [availableStates, setAvailableStates] = useState(getStates('US'));
  const [activeModal, setActiveModal] = useState(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [isLoadingShippingOptions, setIsLoadingShippingOptions] =
    useState(false);
  const [shippingOptionsError, setShippingOptionsError] = useState(null);

  // Only US requires state codes for the Lulu API
  const COUNTRIES_REQUIRING_STATES = ['US'];

  const getCompatibleOptions = (type, currentSelections) => {
    const { bindingOption, paperOption, interiorColorOption } =
      currentSelections;

    switch (type) {
      case 'paper':
        if (bindingOption && interiorColorOption) {
          // Paper type selection based on interior color only
          switch (interiorColorOption.id) {
            case 'standard-black':
              return paperOptions.filter((paper) =>
                ['60-cream-uncoated', '60-white-uncoated'].includes(paper.id)
              );
            case 'standard-color':
              return paperOptions.filter((paper) =>
                ['60-white-uncoated', '80-white-coated'].includes(paper.id)
              );
            case 'premium-color':
              return paperOptions.filter((paper) =>
                ['80-white-coated'].includes(paper.id)
              );
            default:
              return [];
          }
        } else if (bindingOption) {
          // If only binding is selected, show all paper types
          return paperOptions;
        }
        return paperOptions;

      case 'color':
        if (paperOption) {
          // If paper is selected, only show color options that are compatible with the paper
          const compatibleColors = Object.entries(
            printingRules.compatibility.paperByColor
          )
            .filter(([_, papers]) => papers.includes(paperOption.id))
            .map(([colorId]) => colorId);
          return interiorColorOptions.filter((color) =>
            compatibleColors.includes(color.id)
          );
        }
        return interiorColorOptions;

      case 'cover':
        return bindingOption
          ? coverOptions.filter((cover) =>
              printingRules.compatibility.coverByBinding[
                bindingOption.id
              ].includes(cover.id)
            )
          : coverOptions;

      case 'linen':
        return bindingOption && bindingOption.id === 'linen-wrap'
          ? linenOptions
          : [];

      case 'foil':
        return bindingOption && bindingOption.id === 'linen-wrap'
          ? foilOptions
          : [];

      default:
        return [];
    }
  };

  const handleOptionSelect = (type, option) => {
    console.log('🔄 handleOptionSelect:', { type, option });

    // Reset dependent options when changing binding type
    if (type === 'bindingOption') {
      setSelectedOptions((prev) => ({
        ...prev,
        [type]: option,
        paperOption: null,
        coverOption: null,
        linenOption: null,
        foilOption: null,
      }));
      return;
    }

    // Reset paper option when changing interior color type
    if (type === 'interiorColorOption') {
      setSelectedOptions((prev) => ({
        ...prev,
        [type]: option,
        paperOption: null,
      }));
      return;
    }

    setSelectedOptions((prev) => ({
      ...prev,
      [type]: option,
    }));
  };

  const validatePageCount = (binding, count) => {
    if (!binding) return true;
    const rules = printingRules.pageCount.bindings[binding.id];
    return count >= rules.min && count <= rules.max;
  };

  const getValidationError = () => {
    const { bindingOption, pageCount } = selectedOptions;

    if (!bindingOption || !pageCount) return null;

    const rules = printingRules.pageCount.bindings[bindingOption.id];
    if (pageCount < rules.min) {
      return `${bindingOption.title} requires a minimum of ${rules.min} pages`;
    }
    if (pageCount > rules.max) {
      return `${bindingOption.title} has a maximum limit of ${rules.max} pages`;
    }
    return null;
  };

  const handleShippingChange = (field, value) => {
    setShippingDetails((prev) => {
      const newDetails = { ...prev, [field]: value };

      // Handle country change
      if (field === 'countryCode') {
        console.log('🌍 Country changed to:', value);
        // Reset state, shipping method and order summary data when country changes
        newDetails.stateCode = '';
        newDetails.method = '';
        newDetails.estimatedCost = null;
        newDetails.subtotal = null;
        newDetails.shippingCost = null;
        newDetails.tax = null;

        // Get available states for new country
        const states = value ? getStates(value) : [];
        setAvailableStates(states);

        // Fetch shipping options if we have the minimum required fields
        if (
          value &&
          (newDetails.street1 || prev.street1) &&
          (newDetails.city || prev.city) &&
          (newDetails.postalCode || prev.postalCode)
        ) {
          if (isGeneratingPdfs) {
            console.log('⏳ PDFs generating — deferring shipping options fetch');
            pendingShippingDetailsRef.current = newDetails;
            setShippingOptionsError(null);
            setShippingOptions([]);
          } else {
            console.log(
              '✅ Country changed with required fields present, fetching shipping options'
            );
            fetchShippingOptions(newDetails);
          }
        } else {
          console.log('❌ Missing required fields after country change');
          setShippingOptions([]);
        }
      }

      // Clear any previous errors when the user starts typing
      if (newDetails.error) {
        newDetails.error = null;
      }

      console.log('📝 New shipping details:', newDetails);
      return newDetails;
    });
  };

  const handleAddressFieldBlur = (field) => {
    console.log('📫 Address field blur:', field);

    // Always recalculate if state changes or if shipping method is selected
    if (
      field === 'stateCode' ||
      (shippingDetails.method && field !== 'countryCode')
    ) {
      console.log('🔄 Recalculating shipping cost after field change:', field);
      calculateShippingCost(shippingDetails);
    }
  };

  const handleShippingMethodSelect = (method) => {
    console.log('📦 Selected shipping method:', method);
    
    // Store the complete shipping option object for proper API handling
    setShippingDetails((prev) => ({
      ...prev,
      method: method,
      // Immediately update shipping cost in UI based on the selected method
      shippingCost: parseFloat(method.cost_excl_tax || method.cost || 0),
      estimatedCost: (prev.subtotal || 0) + parseFloat(method.cost_excl_tax || method.cost || 0)
    }));
    
    calculateShippingCost({
      ...shippingDetails,
      method: method,
    });
  };

  const fetchShippingOptions = async (details) => {
    console.log('🚀 Fetching shipping options');
    setIsLoadingShippingOptions(true);
    setShippingOptionsError(null);

    try {
      const shippingAddress = {
        street1: details.street1,
        city: details.city,
        state_code: details.stateCode || null,
        country_code: details.countryCode,
        postcode: details.postalCode,
        phone_number: details.phoneNumber,
      };

      console.log('📦 Sending request with address:', shippingAddress);

      const attemptFetch = async () => {
        return await getShippingOptions({
          shippingAddress,
          quantity: details.quantity || 1,
          pageCount: selectedOptions.pageCount || 32,
        });
      };

      let options;
      try {
        options = await attemptFetch();
      } catch (err) {
        console.warn('⚠️ First attempt to get shipping options failed, retrying once...', err?.message);
        // Lightweight single retry after short backoff
        await new Promise((r) => setTimeout(r, 1000));
        options = await attemptFetch();
      }

      console.log('✅ Got shipping options:', options);
      setShippingOptions(options);
      
      // If no method is currently selected, auto-select the cheapest option
      if (!details?.method?.id && Array.isArray(options) && options.length > 0) {
        const cheapest = options.reduce((min, o) => {
          const costO = parseFloat((o?.cost_excl_tax ?? o?.cost ?? Number.POSITIVE_INFINITY));
          const costM = parseFloat((min?.cost_excl_tax ?? min?.cost ?? Number.POSITIVE_INFINITY));
          return costO < costM ? o : min;
        }, options[0]);

        setShippingDetails((prev) => ({
          ...prev,
          method: cheapest,
          shippingCost: parseFloat(cheapest.cost_excl_tax || cheapest.cost || 0),
          estimatedCost: (prev.subtotal || 0) + parseFloat(cheapest.cost_excl_tax || cheapest.cost || 0),
        }));

        // Trigger calculation immediately using the chosen cheapest option
        calculateShippingCost({
          ...details,
          method: cheapest,
        });
      } else if (details?.method?.id) {
        const matched = options.find((o) => o.id === details.method.id);
        if (matched) {
          setShippingDetails((prev) => ({ ...prev, method: matched }));
        } else {
          setShippingDetails((prev) => ({
            ...prev,
            method: '',
            shippingCost: null,
            estimatedCost: prev.subtotal || 0,
          }));
        }
      }
    } catch (error) {
      console.error('❌ Error fetching shipping options:', error);
      setShippingOptionsError(
        error.message || 'Failed to fetch shipping options. Please try again.'
      );
      setShippingOptions([]);
    } finally {
      setIsLoadingShippingOptions(false);
    }
  };

  // When PDFs finish generating, if a shipping fetch was deferred, trigger it now
  useEffect(() => {
    if (!isGeneratingPdfs && pendingShippingDetailsRef.current) {
      const deferred = pendingShippingDetailsRef.current;
      pendingShippingDetailsRef.current = null;
      console.log('▶️ PDFs ready — fetching deferred shipping options');
      fetchShippingOptions(deferred);
    }
  }, [isGeneratingPdfs]);

  // Refetch shipping options when quantity changes and address is complete
  useEffect(() => {
    const d = shippingDetails;
    const addressReady =
      d.street1 && d.city && d.postalCode && d.countryCode && Number(d.quantity) > 0 && (d.countryCode !== 'US' || d.stateCode);
    if (!addressReady) return;

    if (isGeneratingPdfs) {
      pendingShippingDetailsRef.current = d;
      setShippingOptionsError(null);
      setShippingOptions([]);
      return;
    }

    console.log('🔁 Quantity changed with complete address — refetching shipping options');
    fetchShippingOptions(d);
  }, [shippingDetails.quantity]);

  const calculateShippingCost = async (details) => {
    console.log('🚀 Attempting to calculate shipping cost');
    console.log('📦 Current shipping details:', details);

    // Check if we have all required book options
    const podPackageId = getPodPackageId();
    if (!podPackageId) {
      setShippingDetails((prev) => ({
        ...prev,
        error:
          'Please complete your book options before calculating shipping costs.',
        loading: false,
      }));
      return;
    }

    // Check if we have a shipping method selected
    if (!details.method) {
      setShippingDetails((prev) => ({
        ...prev,
        error: 'Please select a shipping method.',
        loading: false,
      }));
      return;
    }

    // Set loading state immediately
    setShippingDetails((prev) => ({
      ...prev,
      loading: true,
      error: null,
      addressWarnings: [],
      suggestedAddress: null,
    }));

    try {
      const result = await createPrintJobCostCalculation({
        quantity: parseInt(details.quantity),
        countryCode: details.countryCode,
        shippingMethod: details.method,
        name: details.name,
        street1: details.street1,
        street2: details.street2 || '',
        city: details.city,
        stateCode: details.stateCode,
        postalCode: details.postalCode,
        phoneNumber: details.phoneNumber,
        podPackageId,
        pageCount: (selectedOptions.pageCount || 100) + 1, // Add +1 to page count for Lulu API
      });

      console.log('✅ Calculated costs from Lulu API:', result);

      if (result.shipping_address?.detail?.errors) {
        const errors = result.shipping_address.detail.errors;
        const warnings = errors.filter(
          (err) => err.type === 'validation_warning'
        );
        const criticalErrors = errors.filter(
          (err) => err.type !== 'validation_warning'
        );

        setShippingDetails((prev) => ({
          ...prev,
          error:
            criticalErrors.length > 0
              ? criticalErrors
                  .map((err) => `${err.field}: ${err.message}`)
                  .join('\n')
              : null,
          addressWarnings: warnings,
          loading: false,
        }));

        if (criticalErrors.length > 0) {
          return;
        }
      }

      // Update the shipping details with the calculated costs
      // Subtotal is ONLY the per-item price × quantity (no printing costs)
      const unitPrice = getUnitPrice();
      const bookSubtotal = unitPrice * (details.quantity || 1);
      const shippingCost = parseFloat(result.shipping_cost?.total_cost_excl_tax) || 0;
      const tax = parseFloat(result.total_tax) || 0;

      setShippingDetails((prev) => ({
        ...prev,
        estimatedCost: bookSubtotal + shippingCost,
        subtotal: bookSubtotal,
        shippingCost,
        tax,
        loading: false,
      }));
    } catch (error) {
      console.error('❌ Error calculating shipping cost:', error);
      setShippingDetails((prev) => ({
        ...prev,
        error:
          error.message ||
          'Failed to calculate shipping cost. Please try again.',
        loading: false,
      }));
    }
  };

  const getPodPackageId = () => {
    console.log('📦 Getting POD package ID');

    // Return null if required options are not selected
    if (
      !selectedOptions.bindingOption ||
      !selectedOptions.interiorColorOption ||
      !selectedOptions.paperOption ||
      !selectedOptions.coverOption
    ) {
      console.log('❌ Missing required options for POD package ID');
      return null;
    }

    // Get the page size from the book, but default to US Trade if not specified
    const pageSize = currentBook?.pageSize || 'us-trade';
    console.log('📏 Book page size:', pageSize);
    
    // Always use US Trade 6x9 format for consistency with our PDFs
    const trimSku = '0600X0900';
    console.log('📏 Using Lulu trim SKU:', trimSku, '(US Trade 6x9)');
    
    // Map interior color options to SKUs
    const colorSku =
      selectedOptions.interiorColorOption.id === 'standard-black' ? 'BW' : 'FC';

    // Map print quality
    const printSku =
      selectedOptions.interiorColorOption.id === 'premium-color'
        ? 'PRE'
        : 'STD';

    // Map binding options to SKUs
    const bindingSku =
      selectedOptions.bindingOption.id === 'perfect-bound'
        ? 'PB'
        : selectedOptions.bindingOption.id === 'case-wrap'
        ? 'CW'
        : selectedOptions.bindingOption.id === 'linen-wrap'
        ? 'LW'
        : 'PB';

    // Map paper options to SKUs
    const paperSku =
      selectedOptions.paperOption.id === '60-cream-uncoated'
        ? '060UC444'
        : selectedOptions.paperOption.id === '60-white-uncoated'
        ? '060UW444'
        : selectedOptions.paperOption.id === '80-white-coated'
        ? '080CW444'
        : '060UW444';

    // Map cover finish options to SKUs
    const finishSku =
      selectedOptions.coverOption?.id === 'matte'
        ? 'M'
        : selectedOptions.coverOption?.id === 'glossy'
        ? 'G'
        : 'M';

    // Map linen options to SKUs (only for linen-wrap binding)
    const linenSku = (() => {
      if (selectedOptions.bindingOption.id !== 'linen-wrap') return 'X';
      // Map ids like 'red-linen' -> 'R', default to Gray 'G' if none selected
      const id = selectedOptions.linenOption?.id || '';
      const map = {
        'red-linen': 'R',
        'navy-linen': 'N',
        'black-linen': 'B',
        'gray-linen': 'G',
        'tan-linen': 'T',
        'forest-linen': 'F',
      };
      if (!id) return 'G';
      return map[id] || 'G';
    })();

    // Map foil options to SKUs (only for linen-wrap binding)
    const foilSku = (() => {
      if (selectedOptions.bindingOption.id !== 'linen-wrap') return 'X';
      const id = selectedOptions.foilOption?.id || '';
      const map = {
        'gold-foil': 'G',
        'black-foil': 'B',
        'white-foil': 'W',
      };
      return map[id] || 'X';
    })();

    // Construct the full SKU following Lulu's format:
    // [TrimSKU][ColorSKU][PrintSKU][BindSKU][PaperSKU][FinishSKU][LinenSKU][FoilSKU]
    const podPackageId = `${trimSku}${colorSku}${printSku}${bindingSku}${paperSku}${finishSku}${linenSku}${foilSku}`;
    console.log('Generated POD Package ID:', podPackageId);
    return podPackageId;
  };

  const getDisabledReason = (option) => {
    switch (option.type) {
      case 'binding':
        return null; // Always enabled

      case 'interior':
        if (!selectedOptions.bindingOption) {
          return 'Select binding type first';
        }
        return null;

      case 'paper':
        if (!selectedOptions.bindingOption) {
          return 'Select binding type first';
        }
        if (!selectedOptions.interiorColorOption) {
          return 'Select interior color first';
        }

        // Only check paper compatibility if we have a specific paper option
        if (option.id) {
          // Paper type restrictions based on interior color
          switch (selectedOptions.interiorColorOption.id) {
            case 'standard-black':
              if (
                !['60-cream-uncoated', '60-white-uncoated'].includes(option.id)
              ) {
                return 'Not available with Standard Black & White interior';
              }
              break;
            case 'standard-color':
              if (
                !['60-white-uncoated', '80-white-coated'].includes(option.id)
              ) {
                return 'Not available with Standard Color interior';
              }
              break;
            case 'premium-color':
              if (!['80-white-coated'].includes(option.id)) {
                return 'Not available with Premium Color interior';
              }
              break;
          }
        }

        return null;

      case 'cover':
        if (!selectedOptions.bindingOption) {
          return 'Select binding type first';
        }
        if (!selectedOptions.interiorColorOption) {
          return 'Select interior color first';
        }
        if (!selectedOptions.paperOption) {
          return 'Select paper type first';
        }
        return null;

      case 'linen':
      case 'foil':
        if (!selectedOptions.bindingOption) {
          return 'Select binding type first';
        }
        if (!selectedOptions.interiorColorOption) {
          return 'Select interior color first';
        }
        if (!selectedOptions.paperOption) {
          return 'Select paper type first';
        }
        if (!selectedOptions.coverOption) {
          return 'Select cover finish first';
        }
        return null;

      default:
        return 'Option type not recognized';
    }
  };

  useEffect(() => {
    console.log('🔄 useEffect: Shipping details changed');
    console.log('📦 Current shipping details:', shippingDetails);

    if (
      shippingDetails.countryCode &&
      shippingDetails.method &&
      shippingDetails.quantity
    ) {
      console.log(
        '✅ All required fields present, calculating shipping cost...'
      );
      calculateShippingCost(shippingDetails);
    } else {
      console.log('❌ Missing required fields for calculation');
    }
  }, [
    shippingDetails.countryCode,
    shippingDetails.method,
    shippingDetails.quantity,
  ]);

  const addressFields = {
    street1: (
      <input
        id="street1"
        name="street1"
        type="text"
        value={shippingDetails.street1}
        onChange={(e) => handleShippingChange('street1', e.target.value)}
        onBlur={() => handleAddressFieldBlur('street1')}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        placeholder="123 Main St"
        required
      />
    ),
    street2: (
      <input
        id="street2"
        name="street2"
        type="text"
        value={shippingDetails.street2}
        onChange={(e) => handleShippingChange('street2', e.target.value)}
        onBlur={() => handleAddressFieldBlur('street2')}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        placeholder="Apt 4B"
      />
    ),
    city: (
      <input
        id="city"
        name="city"
        type="text"
        value={shippingDetails.city}
        onChange={(e) => handleShippingChange('city', e.target.value)}
        onBlur={() => handleAddressFieldBlur('city')}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        placeholder="New York"
        required
      />
    ),
    postalCode: (
      <input
        id="postal-code"
        name="postal-code"
        type="text"
        value={shippingDetails.postalCode}
        onChange={(e) => handleShippingChange('postalCode', e.target.value)}
        onBlur={() => handleAddressFieldBlur('postalCode')}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        required
      />
    ),
    state: shippingDetails.countryCode === 'US' && (
      <div>
        <label
          htmlFor="state"
          className="block text-sm font-medium text-gray-700"
        >
          State
        </label>
        <select
          id="state"
          name="state"
          value={shippingDetails.stateCode}
          onChange={(e) => handleShippingChange('stateCode', e.target.value)}
          onBlur={() => handleAddressFieldBlur('stateCode')}
          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          required
        >
          <option value="">Select a state</option>
          {availableStates.map((state) => (
            <option key={state.code} value={state.code}>
              {state.name}
            </option>
          ))}
        </select>
      </div>
    ),
  };

  const isCountryEnabled = Boolean(
    shippingDetails.street1 &&
    shippingDetails.city &&
    shippingDetails.postalCode &&
    Number(shippingDetails.quantity) > 0
  );
  const isShippingFetchQueued = isGeneratingPdfs && !!pendingShippingDetailsRef.current;
  const estimatedDeliveryText = (() => {
    const m = shippingDetails.method;
    if (!m) return '';
    const min = m.minDeliveryDate || m.min_delivery_date;
    const max = m.maxDeliveryDate || m.max_delivery_date;
    if (!min && !max) return '';
    const fmt = (d) => {
      const dt = new Date(d);
      return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    };
    return min && max ? `${fmt(min)} - ${fmt(max)}` : fmt(min || max);
  })();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Top-aligned navigation controls to match other steps */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ship</h1>
          <p className="text-gray-600">Select print options, enter shipping details, and complete checkout.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowTutorialModal(true)}
            className="px-4 py-2 rounded-md bg-yellow-100 text-yellow-900 hover:bg-yellow-200 font-medium"
          >
            Tutorial
          </button>
          <button
            onClick={() => navigate(`/print/${bookId}`)}
            className="px-4 py-2 rounded-md border text-gray-700 hover:bg-gray-50"
          >
            Back to Print
          </button>
        </div>
      </div>

      <TutorialModal
        isOpen={showTutorialModal}
        onClose={() => setShowTutorialModal(false)}
        defaultTabId={6}
      />
      <div className="grid grid-cols-12 gap-8">
        {/* Left Column - Print Options (7/12) */}
        <div className="col-span-12 lg:col-span-7">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Print Options
          </h2>

          {/* Print Options in Single Row */}
          <div className="grid grid-cols-4 gap-6 mb-8">
            {/* Binding Type */}
            <div
              onClick={() => setActiveModal('binding')}
              className="relative rounded-lg overflow-hidden cursor-pointer transition-all border border-gray-200 hover:border-indigo-400 hover:shadow-md bg-white"
            >
              <div className="flex flex-col h-full">
                <div className="text-center p-3 border-b border-gray-100">
                  <h3 className="font-medium text-gray-700">Binding Type</h3>
                </div>
                <div className="flex-grow bg-gray-50 p-4">
                  {selectedOptions.bindingOption ? (
                    <div className="h-full">
                      <div className="aspect-w-4 aspect-h-3 mb-3">
                        <img
                          src={selectedOptions.bindingOption.image}
                          alt={selectedOptions.bindingOption.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="text-center">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {selectedOptions.bindingOption.title}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {selectedOptions.bindingOption.description}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <p className="text-sm text-gray-500">Select binding</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Interior Color */}
            <div
              onClick={() =>
                !getDisabledReason({ type: 'interior' }) &&
                setActiveModal('interior')
              }
              className={`
                relative rounded-lg overflow-hidden transition-all border
                ${
                  getDisabledReason({ type: 'interior' })
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                    : 'border-gray-200 hover:border-indigo-400 hover:shadow-md bg-white cursor-pointer'
                }
              `}
            >
              <div className="flex flex-col h-full">
                <div className="text-center p-3 border-b border-gray-100">
                  <h3
                    className={`font-medium ${
                      getDisabledReason({ type: 'interior' })
                        ? 'text-gray-400'
                        : 'text-gray-700'
                    }`}
                  >
                    Interior Color
                  </h3>
                </div>
                <div className="flex-grow bg-gray-50 p-4">
                  {selectedOptions.interiorColorOption ? (
                    <div className="h-full">
                      <div className="aspect-w-4 aspect-h-3 mb-3">
                        <img
                          src={selectedOptions.interiorColorOption.image}
                          alt={selectedOptions.interiorColorOption.title}
                          className={`w-full h-full object-contain ${
                            getDisabledReason({ type: 'interior' })
                              ? 'grayscale'
                              : ''
                          }`}
                        />
                      </div>
                      <div className="text-center">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {selectedOptions.interiorColorOption.title}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {selectedOptions.interiorColorOption.description}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <p
                        className={`text-sm ${
                          getDisabledReason({ type: 'interior' })
                            ? 'text-gray-400'
                            : 'text-gray-500'
                        }`}
                      >
                        {getDisabledReason({ type: 'interior' })
                          ? 'Not available'
                          : 'Select color'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Paper Type */}
            <div
              onClick={() =>
                !getDisabledReason({ type: 'paper' }) && setActiveModal('paper')
              }
              className={`
                relative rounded-lg overflow-hidden transition-all border
                ${
                  getDisabledReason({ type: 'paper' })
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                    : 'border-gray-200 hover:border-indigo-400 hover:shadow-md bg-white cursor-pointer'
                }
              `}
            >
              <div className="flex flex-col h-full">
                <div className="text-center p-3 border-b border-gray-100">
                  <h3
                    className={`font-medium ${
                      getDisabledReason({ type: 'paper' })
                        ? 'text-gray-400'
                        : 'text-gray-700'
                    }`}
                  >
                    Paper Type
                  </h3>
                </div>
                <div className="flex-grow bg-gray-50 p-4">
                  {selectedOptions.paperOption ? (
                    <div className="h-full">
                      <div className="aspect-w-4 aspect-h-3 mb-3">
                        <img
                          src={selectedOptions.paperOption.image}
                          alt={selectedOptions.paperOption.title}
                          className={`w-full h-full object-contain ${
                            getDisabledReason({ type: 'paper' })
                              ? 'grayscale'
                              : ''
                          }`}
                        />
                      </div>
                      <div className="text-center">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {selectedOptions.paperOption.title}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {selectedOptions.paperOption.description}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <p
                        className={`text-sm ${
                          getDisabledReason({ type: 'paper' })
                            ? 'text-gray-400'
                            : 'text-gray-500'
                        }`}
                      >
                        {getDisabledReason({ type: 'paper' })
                          ? 'Not available'
                          : 'Select paper'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Cover Finish */}
            <div
              onClick={() =>
                !getDisabledReason({ type: 'cover' }) && setActiveModal('cover')
              }
              className={`
                relative rounded-lg overflow-hidden transition-all border
                ${
                  getDisabledReason({ type: 'cover' })
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                    : 'border-gray-200 hover:border-indigo-400 hover:shadow-md bg-white cursor-pointer'
                }
              `}
            >
              <div className="flex flex-col h-full">
                <div className="text-center p-3 border-b border-gray-100">
                  <h3
                    className={`font-medium ${
                      getDisabledReason({ type: 'cover' })
                        ? 'text-gray-400'
                        : 'text-gray-700'
                    }`}
                  >
                    Cover Finish
                  </h3>
                </div>
                <div className="flex-grow bg-gray-50 p-4">
                  {selectedOptions.coverOption ? (
                    <div className="h-full">
                      <div className="aspect-w-4 aspect-h-3 mb-3">
                        <img
                          src={selectedOptions.coverOption.image}
                          alt={selectedOptions.coverOption.title}
                          className={`w-full h-full object-contain ${
                            getDisabledReason({ type: 'cover' })
                              ? 'grayscale'
                              : ''
                          }`}
                        />
                      </div>
                      <div className="text-center">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {selectedOptions.coverOption.title}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {selectedOptions.coverOption.description}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <p
                        className={`text-sm ${
                          getDisabledReason({ type: 'cover' })
                            ? 'text-gray-400'
                            : 'text-gray-500'
                        }`}
                      >
                        {getDisabledReason({ type: 'cover' })
                          ? 'Not available'
                          : 'Select cover'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* PDF Upload section after print options */}
          {uiState.showUploadSection && (
            <div className="border-t pt-8 mt-8 mb-12">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold">Your Book Files</h2>
                  <p className="text-gray-600">We prepare your Cover and Interior PDFs based on your selections.</p>
                </div>
                <button
                  type="button"
                  onClick={() => generatePdfs(true)}
                  disabled={isGeneratingPdfs}
                  className={`ml-4 inline-flex items-center px-3 py-1.5 rounded-md text-sm ${isGeneratingPdfs ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} text-white`}
                >
                  {isGeneratingPdfs ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    'Regenerate PDFs'
                  )}
                </button>
              </div>
              <PDFShippingPreview 
                bookId={bookId}
                onReady={handlePDFsReady}
                savedPdfUrls={savedPdfUrls}
                loading={isGeneratingPdfs}
              />
            </div>
          )}

          {/* Shipping Information */}
          {uiState.showShippingSection && (
            <div className="border-t pt-8 mt-12">
              <h2 className="text-xl font-semibold mb-6">Shipping Details</h2>
              <p className="text-gray-600 mb-8">Please provide your shipping information to complete your order.</p>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="space-y-6">
                  {/* Shipping form fields */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={shippingDetails.name}
                      onChange={(e) => handleShippingChange('name', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={shippingDetails.phoneNumber}
                      onChange={(e) =>
                        handleShippingChange('phoneNumber', e.target.value)
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="123-456-7890"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Street Address
                    </label>
                    {addressFields.street1}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Apartment, suite, etc.
                    </label>
                    {addressFields.street2}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      City
                    </label>
                    {addressFields.city}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Postal Code
                      </label>
                      {addressFields.postalCode}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Quantity
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={shippingDetails.quantity}
                        onChange={(e) =>
                          handleShippingChange(
                            'quantity',
                            parseInt(e.target.value) || 1
                          )
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>

                    <div className={shippingDetails.countryCode === 'US' ? '' : 'col-span-2'}>
                      <label
                        htmlFor="country"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Country
                      </label>
                      <select
                        id="country"
                        name="country"
                        value={shippingDetails.countryCode}
                        onChange={(e) =>
                          handleShippingChange('countryCode', e.target.value)
                        }
                        className={`mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${isCountryEnabled ? 'bg-white' : 'bg-gray-100 cursor-not-allowed'}`}
                        disabled={!isCountryEnabled}
                        required
                      >
                        <option value="">Select a country</option>
                        {countries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                      {!isCountryEnabled && (
                        <p className="mt-1 text-xs text-gray-500">Fill out street, city, postal code, and quantity first.</p>
                      )}
                    </div>

                    {addressFields.state}
                  </div>

                  <div className="mt-6">
                    <h3 className="text-base font-medium text-gray-900">
                      Shipping Method
                    </h3>
                    <div className="mt-4 grid grid-cols-2 gap-4">
                      {isShippingFetchQueued ? (
                        <div className="col-span-2 text-center py-4">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-indigo-600"></div>
                          <p className="mt-2 text-sm text-gray-500">
                            Fetching options once book files finish preparing...
                          </p>
                        </div>
                      ) : isLoadingShippingOptions ? (
                        <div className="col-span-2 text-center py-4">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-indigo-600"></div>
                          <p className="mt-2 text-sm text-gray-500">
                            Loading shipping options...
                          </p>
                        </div>
                      ) : shippingOptionsError ? (
                        <div className="col-span-2">
                          <div className="bg-red-50 p-4 rounded-md">
                            <div className="flex">
                              <div className="flex-shrink-0">
                                <svg
                                  className="h-5 w-5 text-red-400"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">
                                  Error loading shipping options
                                </h3>
                                <p className="mt-2 text-sm text-red-700">
                                  {shippingOptionsError}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : shippingOptions.length > 0 ? (
                        shippingOptions.map((option) => (
                          <div
                            key={option.id}
                            className={`flex items-start p-4 border rounded-lg hover:bg-gray-50 ${
                              option.isHomeOnly
                                ? 'border-indigo-200'
                                : option.isBusinessOnly
                                ? 'border-blue-200'
                                : 'border-gray-200'
                            }`}
                          >
                            <input
                              id={option.id}
                              name="shipping-method"
                              type="radio"
                              value={option.id}
                              checked={shippingDetails.method?.id === option.id}
                              onChange={() => handleShippingMethodSelect(option)
                              }
                              className="h-4 w-4 mt-1 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <label
                              htmlFor={option.id}
                              className="ml-3 flex-1 cursor-pointer"
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <span className="block text-sm font-medium text-gray-900">
                                    {option.title || option.carrier_service_name || 'Shipping Option'}
                                  </span>
                                  <span className="block text-sm text-gray-500 mt-1">
                                    Delivery: {option.minDeliveryDate || option.min_delivery_date || '-'} -{' '}
                                    {option.maxDeliveryDate || option.max_delivery_date || '-'}
                                  </span>
                                  <span className="block text-sm text-gray-500">
                                    Transit time: {option.transitTime || option.transit_time || '-'}{' '}
                                    {(option.transitTime || option.transit_time) === 1 ? 'day' : 'days'}
                                  </span>
                                </div>
                                <span className="text-sm font-medium text-gray-900">
                                  {option.cost_excl_tax ? `$${option.cost_excl_tax.toFixed(2)}` : option.cost ? `$${option.cost.toFixed(2)}` : 'Price unavailable'}
                                </span>
                              </div>
                              {(option.isHomeOnly ||
                                option.isBusinessOnly ||
                                option.isPostboxOk) && (
                                <div className="mt-2 flex gap-2">
                                  {option.isHomeOnly && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                      Residential Only
                                    </span>
                                  )}
                                  {option.isBusinessOnly && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                      Business Only
                                    </span>
                                  )}
                                  {option.isPostboxOk && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                      PO Box OK
                                    </span>
                                  )}
                                </div>
                              )}
                            </label>
                          </div>
                        ))
                      ) : (
                        <div className="col-span-2 text-center py-4 text-sm text-gray-500">
                          Please enter a complete shipping address to see available
                          shipping options.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Order Summary */}
        <div className="col-span-12 lg:col-span-5">
          <div className="sticky top-4 mt-4 lg:mt-0">
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-medium text-gray-900">Order Summary</h2>

              <div className="mt-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="p-4 space-y-4">
                  {/* Address Warnings */}
                  {shippingDetails.addressWarnings?.length > 0 && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-5 w-5 text-yellow-400"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">
                            Address Updates Available
                          </h3>
                          <div className="mt-2 text-sm text-yellow-700">
                            {shippingDetails.addressWarnings.map(
                              (warning, index) => (
                                <p key={index}>{warning.message}</p>
                              )
                            )}
                          </div>
                          {shippingDetails.suggestedAddress && (
                            <div className="mt-4">
                              <h4 className="text-sm font-medium text-yellow-800">
                                Suggested Address:
                              </h4>
                              <div className="mt-1 text-sm text-yellow-700">
                                <p>{shippingDetails.suggestedAddress.street1}</p>
                                {shippingDetails.suggestedAddress.street2 && (
                                  <p>
                                    {shippingDetails.suggestedAddress.street2}
                                  </p>
                                )}
                                <p>
                                  {shippingDetails.suggestedAddress.city},{' '}
                                  {shippingDetails.suggestedAddress.state_code}{' '}
                                  {shippingDetails.suggestedAddress.postcode}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Cost Breakdown */}
                  {shippingDetails.loading ? (
                    <div className="animate-pulse space-y-4">
                      <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <div className="h-4 bg-gray-200 rounded w-24"></div>
                          <div className="h-4 bg-gray-200 rounded w-16"></div>
                        </div>
                        <div className="flex justify-between">
                          <div className="h-4 bg-gray-200 rounded w-24"></div>
                          <div className="h-4 bg-gray-200 rounded w-16"></div>
                        </div>
                        <div className="flex justify-between">
                          <div className="h-4 bg-gray-200 rounded w-24"></div>
                          <div className="h-4 bg-gray-200 rounded w-16"></div>
                        </div>
                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex justify-between">
                            <div className="h-5 bg-gray-200 rounded w-24"></div>
                            <div className="h-5 bg-gray-200 rounded w-24"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {shippingDetails.error ? (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg
                                className="h-5 w-5 text-red-400"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-red-800">
                                Error calculating shipping cost
                              </h3>
                              <div className="mt-2 text-sm text-red-700">
                                {shippingDetails.error
                                  .split('\n')
                                  .map((err, i) => (
                                    <p key={i}>{err}</p>
                                  ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm text-gray-600">
                              <p>Subtotal</p>
                              <p>
                                ${shippingDetails.subtotal?.toFixed(2) || '0.00'}
                                {shippingDetails.quantity > 0 && (
                                  <span className="block text-xs text-gray-500">
                                    {(
                                      (shippingDetails.subtotal || 0) /
                                      (shippingDetails.quantity || 1)
                                    ).toFixed(2)} per book
                                  </span>
                                )}
                              </p>
                            </div>
                            <div className="flex justify-between text-sm text-gray-600">
                              <p>Shipping</p>
                              <p>
                                $
                                {shippingDetails.shippingCost?.toFixed(2) ||
                                  '0.00'}
                              </p>
                            </div>
                            {estimatedDeliveryText && (
                              <div className="flex justify-between text-sm text-gray-600">
                                <p>Estimated delivery</p>
                                <p>{estimatedDeliveryText}</p>
                              </div>
                            )}
                            <div className="flex justify-between text-base font-medium text-gray-900 pt-4 border-t border-gray-200">
                              <p>Total</p>
                              <p>
                                $
                                {shippingDetails.estimatedCost?.toFixed(2) ||
                                  '0.00'}
                              </p>
                            </div>
                            {shippingDetails.estimatedCost > 0 && (
                              <div className="mt-4">
                                <button
                                  ref={proceedButtonRef}
                                  type="button"
                                  disabled={isCreatingOrder}
                                  onClick={async () => {
                                    try {
                                      setIsCreatingOrder(true);
                                      const toastId = notify.loading('Preparing checkout...', { toastId: `ship-checkout-${bookId}` });

                                      if (!currentBook) {
                                        notify.dismiss(toastId);
                                        notify.error('Book data not available', { toastId: `ship-checkout-error-${bookId}` });
                                        return;
                                      }

                                      // Format shipping address
                                      const shippingAddress = {
                                        name: shippingDetails.name,
                                        street1: shippingDetails.street1,
                                        street2: shippingDetails.street2 || '',
                                        city: shippingDetails.city,
                                        country_code: shippingDetails.countryCode,
                                        state_code: shippingDetails.stateCode || '',
                                        postcode: shippingDetails.postalCode,
                                        phone_number: shippingDetails.phoneNumber,
                                      };

                                      // Get POD package ID for the print specification
                                      const podPackageId = getPodPackageId();
                                      if (!podPackageId) {
                                        notify.dismiss(toastId);
                                        throw new Error('Invalid print specification');
                                      }

                                      // Compute payment amount (fallback to subtotal + shipping; tax excluded)
                                      const estimated = (typeof shippingDetails.estimatedCost === 'number' && shippingDetails.estimatedCost > 0)
                                        ? shippingDetails.estimatedCost
                                        : ((shippingDetails.subtotal || 0) + (shippingDetails.shippingCost || 0));
                                      const amountInCents = Math.round((estimated || 0) * 100);
                                      if (!amountInCents || amountInCents <= 0) {
                                        notify.dismiss(toastId);
                                        notify.error('Unable to determine payment amount. Please re-calculate shipping.', { toastId: `ship-checkout-error-${bookId}` });
                                        setIsCreatingOrder(false);
                                        return;
                                      }

                                      // Persist this order with the checkout session; PDFs will be generated post-payment
                                      const orderData = {
                                        email: 'user@example.com', // TODO: replace with auth user email
                                        title: currentBook?.title || 'Untitled Book',
                                        quantity: shippingDetails.quantity,
                                        printSpecId: podPackageId,
                                        shippingAddress,
                                        shippingLevel: shippingDetails.method,
                                        // PDFs omitted intentionally; will be generated after payment
                                        bookId,
                                      };

                                      const auth = getAuth();
                                      const user = auth.currentUser;
                                      try { localStorage.setItem('sbw.printBinding', selectedOptions.bindingOption?.id || 'perfect-bound'); } catch (_) {}
                                      const response = await fetch(getCloudFunctionUrl('createStripeCheckoutSession'), {
                                        method: 'POST',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Authorization': `Bearer ${user ? await user.getIdToken() : ''}`
                                        },
                                        body: JSON.stringify({
                                          amount: amountInCents,
                                          quantity: shippingDetails.quantity,
                                          fixedMarkup: 0,
                                          currency: 'usd',
                                          customerEmail: user?.email || orderData.email,
                                          productName: `Print Job for ${orderData.title}`,
                                          successUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
                                          cancelUrl: `${window.location.origin}/ship/${bookId}?checkout=cancel`,
                                          orderData,
                                          unitBookPriceCents: Math.round(getUnitPrice() * 100)
                                        })
                                      });

                                      notify.dismiss(toastId);

                                      if (!response.ok) {
                                        const errJson = await response.json().catch(() => ({}));
                                        throw new Error(errJson.error || 'Failed to create checkout session');
                                      }

                                      const { url } = await response.json();
                                      if (url) {
                                        notify.info('Redirecting to Stripe...', { toastId: `ship-checkout-redirect-${bookId}` });
                                        window.location.href = url;
                                      } else {
                                        throw new Error('Stripe checkout URL missing');
                                      }
                                    } catch (error) {
                                      console.error('Error creating print job:', error);
                                      notify.error(error.message || 'Failed to create print job', { toastId: `ship-checkout-error-${bookId}` });
                                      // Only reset loading state on error
                                      setIsCreatingOrder(false);
                                    }
                                  }}
                                  className={`w-full py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${isCreatingOrder ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} text-white`}
                                >
                                  {isCreatingOrder ? (
                                    <>
                                      <svg className="inline animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      Redirecting to Payment...
                                    </>
                                  ) : (
                                    'Proceed to Payment'
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Style Selection Modals */}
      <StyleModal
        isOpen={activeModal === 'binding'}
        onClose={() => setActiveModal(null)}
        title="Select Binding Type"
        description="Choose how your book will be bound"
        options={bindingOptions}
        selectedOption={selectedOptions.bindingOption}
        onSelect={(option) => handleOptionSelect('bindingOption', option)}
        getDisabledReason={getDisabledReason}
      />

      <StyleModal
        isOpen={activeModal === 'interior'}
        onClose={() => setActiveModal(null)}
        title="Select Interior Color"
        description="Choose your book's interior printing style"
        options={interiorColorOptions}
        selectedOption={selectedOptions.interiorColorOption}
        onSelect={(option) => handleOptionSelect('interiorColorOption', option)}
        getDisabledReason={getDisabledReason}
      />

      <StyleModal
        isOpen={activeModal === 'paper'}
        onClose={() => setActiveModal(null)}
        title="Select Paper Type"
        description="Choose your book's paper quality"
        options={getCompatibleOptions('paper', selectedOptions)}
        selectedOption={selectedOptions.paperOption}
        onSelect={(option) => handleOptionSelect('paperOption', option)}
        getDisabledReason={getDisabledReason}
      />

      <StyleModal
        isOpen={activeModal === 'cover'}
        onClose={() => setActiveModal(null)}
        title="Select Cover Finish"
        description="Choose your book's cover finish"
        options={coverOptions}
        selectedOption={selectedOptions.coverOption}
        onSelect={(option) => handleOptionSelect('coverOption', option)}
        getDisabledReason={getDisabledReason}
      />

      {selectedOptions.bindingOption?.id === 'linen-wrap' && (
        <StyleModal
          isOpen={activeModal === 'linen'}
          onClose={() => setActiveModal(null)}
          title="Select Linen Color"
          description="Choose your book's linen color"
          options={linenOptions}
          selectedOption={selectedOptions.linenOption}
          onSelect={(option) => handleOptionSelect('linenOption', option)}
          getDisabledReason={getDisabledReason}
        />
      )}

      {selectedOptions.bindingOption?.id === 'linen-wrap' && (
        <StyleModal
          isOpen={activeModal === 'foil'}
          onClose={() => setActiveModal(null)}
          title="Select Foil Color"
          description="Choose your book's foil color"
          options={foilOptions}
          selectedOption={selectedOptions.foilOption}
          onSelect={(option) => handleOptionSelect('foilOption', option)}
          getDisabledReason={getDisabledReason}
        />
      )}
    </div>
  );
}
